<script setup lang="ts">
import type { ApiKeyToken } from '@/types/apiKeys'

const props = defineProps<{
  open: boolean
  token: ApiKeyToken | null
  isRevoking: boolean
}>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'confirm'): void
}>()

const { t } = useI18n()
const { formatNumber } = useNumber()
const { localDate } = useDate()

const lastUsedLabel = computed(() =>
  props.token?.lastUsedAt ? localDate(new Date(props.token.lastUsedAt)) : t('pages.settings.apiKeys.list.neverUsed')
)
</script>

<template>
  <AlertDialog :open="open" @update:open="(v) => emit('update:open', v)">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>
          {{ t('pages.settings.apiKeys.revoke.title', { name: token?.name }) }}
        </AlertDialogTitle>
        <AlertDialogDescription>
          {{ t('pages.settings.apiKeys.revoke.description') }}
        </AlertDialogDescription>
      </AlertDialogHeader>

      <div class="grid grid-cols-2 gap-3 rounded-md border bg-muted px-4 py-3 text-sm">
        <div class="flex flex-col gap-0.5">
          <span class="text-muted-foreground">{{ t('pages.settings.apiKeys.list.lastUsed') }}</span>
          <span class="font-medium text-foreground">{{ lastUsedLabel }}</span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-muted-foreground">{{ t('pages.settings.apiKeys.list.callsThisMonth') }}</span>
          <span class="font-medium text-foreground">{{ formatNumber(token?.callsCurrentMonth) }}</span>
        </div>
        <div class="col-span-2 flex flex-col gap-0.5">
          <span class="text-muted-foreground">{{ t('pages.settings.apiKeys.revoke.identifier') }}</span>
          <span class="font-mono text-foreground">{{ token?.prefix }}</span>
        </div>
      </div>

      <AlertDialogFooter>
        <AlertDialogCancel>{{ t('pages.settings.apiKeys.revoke.cancel') }}</AlertDialogCancel>
        <Button variant="destructive" :disabled="isRevoking" @click="emit('confirm')">
          {{ t('pages.settings.apiKeys.revoke.confirm') }}
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
